import {
  DEPOSIT_CEILING_CENTS,
  DEPOSIT_FLOOR_CENTS,
  DEPOSIT_MAX_PRESETS,
  DEPOSIT_SETTINGS_ISSUE_MESSAGES,
  type DepositSettings,
  depositSettingsIssue,
  formatAmount,
  parseAmount,
  USD,
} from "@loot-os/api-contract";
import { Button } from "@loot-os/ui/components/button";
import { Input } from "@loot-os/ui/components/input";
import { Label } from "@loot-os/ui/components/label";
import type { ReactNode } from "react";
import { useState } from "react";

import { useUnsavedChangesGuard } from "../lib/useUnsavedChangesGuard";
import { UnsavedChangesDialog } from "./UnsavedChangesDialog";

/** Dollars as an editor types them: "$50", "$1,000", "$12.50". */
function dollars(cents: number): string {
  return formatAmount(cents, USD).replace(/\.00$/, "");
}

/**
 * One amount in the presets field: grouped by thousands ("$1,000", "$12,500.50") or not ("$50",
 * "12.50"), with or without the "$".
 */
const PRESET_AMOUNT = /\$?\s*\d{1,3}(?:,\d{3})+(?:\.\d+)?(?!\d)|\$?\s*\d+(?:\.\d+)?/g;

/** The form's text, as typed. */
interface Draft {
  presets: string;
  min: string;
  max: string;
}

function draftOf(settings: DepositSettings): Draft {
  return {
    presets: settings.presetsCents.map(dollars).join(", "),
    min: dollars(settings.minCents),
    max: dollars(settings.maxCents),
  };
}

/**
 * The draft as settings, or why it isn't any: an amount that doesn't read as dollars, one outside
 * the platform's bounds, too many presets, or a rule the settings break (`depositSettingsIssue`).
 */
function settingsOf(draft: Draft): { settings: DepositSettings } | { error: string } {
  // A preset list is amounts separated by commas or spaces: each amount is read whole, so a
  // comma that groups thousands ("$1,000") stays inside it, and "50,100" is two presets.
  const parts = draft.presets.match(PRESET_AMOUNT) ?? [];
  // Anything else typed (a letter, a stray symbol) is not an amount, and says so below.
  const leftover = draft.presets.replace(PRESET_AMOUNT, "");
  const presetsCents = parts.map((part) => parseAmount(part, USD));
  const minCents = parseAmount(draft.min, USD);
  const maxCents = parseAmount(draft.max, USD);
  if (presetsCents.length === 0) return { error: "Add at least one preset." };
  if (presetsCents.length > DEPOSIT_MAX_PRESETS) {
    return { error: `Offer at most ${DEPOSIT_MAX_PRESETS} presets.` };
  }
  if (
    /[^,\s]/.test(leftover) ||
    presetsCents.some((cents) => cents === null) ||
    minCents === null ||
    maxCents === null
  ) {
    return { error: "Write every amount in dollars, like $50 or $12.50." };
  }
  // Named as read, so "50,100" shows it was taken as one amount ($50,100), not two.
  const cents = [...(presetsCents as number[]), minCents, maxCents];
  const under = cents.find((c) => c < DEPOSIT_FLOOR_CENTS);
  if (under !== undefined) {
    return { error: `${dollars(under)} is under the ${dollars(DEPOSIT_FLOOR_CENTS)} floor.` };
  }
  const over = cents.find((c) => c > DEPOSIT_CEILING_CENTS);
  if (over !== undefined) {
    return {
      error: `${dollars(over)} is over the ${dollars(DEPOSIT_CEILING_CENTS)} limit. Separate presets with a comma and a space.`,
    };
  }
  const settings = { presetsCents: presetsCents as number[], minCents, maxCents };
  const issue = depositSettingsIssue(settings);
  return issue ? { error: DEPOSIT_SETTINGS_ISSUE_MESSAGES[issue] } : { settings };
}

function isSame(a: DepositSettings, b: DepositSettings): boolean {
  return (
    a.minCents === b.minCents &&
    a.maxCents === b.maxCents &&
    a.presetsCents.join() === b.presetsCents.join()
  );
}

/** The settings as a page summarizes them: the presets, and the least and most. */
export function DepositSettingsSummary({ deposit }: { deposit: DepositSettings }) {
  return (
    <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
      <div>
        <dt className="text-xs text-muted-foreground">Presets</dt>
        <dd>{deposit.presetsCents.map(dollars).join(", ")}</dd>
      </div>
      <div>
        <dt className="text-xs text-muted-foreground">Minimum</dt>
        <dd>{dollars(deposit.minCents)}</dd>
      </div>
      <div>
        <dt className="text-xs text-muted-foreground">Maximum</dt>
        <dd>{dollars(deposit.maxCents)}</dd>
      </div>
    </dl>
  );
}

/**
 * What a deposit may be, as one form: the presets the Deposit dialog offers and the least and
 * most a shopper may type, in dollars. Shared by the platform default (`/deposits`) and an
 * environment's override (Storefront → Payments → Deposits), which differ only in the `footer`.
 * Remount with `key` when the saved value changes. A navigation away with unsaved changes asks
 * first (`useUnsavedChangesGuard`), offering Save and leave while the draft is valid.
 */
export function DepositSettingsForm({
  deposit,
  canEdit,
  saving,
  onSave,
  footer,
}: {
  deposit: DepositSettings;
  canEdit: boolean;
  saving: boolean;
  /** Saves; resolves once saved and rejects on failure (`mutateAsync`), whose toast says why. */
  onSave: (deposit: DepositSettings) => Promise<unknown>;
  footer?: ReactNode;
}) {
  const [draft, setDraft] = useState<Draft>(() => draftOf(deposit));
  const result = settingsOf(draft);
  const dirty = "settings" in result ? !isSame(result.settings, deposit) : true;

  const guard = useUnsavedChangesGuard({
    dirty: canEdit && dirty,
    onSave: "settings" in result ? () => onSave(result.settings) : undefined,
    onDiscard: () => setDraft(draftOf(deposit)),
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    // A failure is the mutation's toast; the draft stays to fix.
    if ("settings" in result) onSave(result.settings).catch(() => {});
  };

  return (
    <form className="grid gap-5" onSubmit={submit}>
      <UnsavedChangesDialog guard={guard} />
      <div className="grid gap-4 sm:grid-cols-[2fr_1fr_1fr]">
        <div className="grid gap-2">
          <Label htmlFor="deposit-presets">Presets</Label>
          <Input
            id="deposit-presets"
            value={draft.presets}
            disabled={!canEdit}
            onChange={(e) => setDraft({ ...draft, presets: e.target.value })}
            placeholder="$50, $100, $250, $500, $1,000"
          />
          <p className="text-xs text-muted-foreground">
            The amounts a shopper taps in Deposit, smallest first, up to {DEPOSIT_MAX_PRESETS}.
          </p>
        </div>
        <div className="grid gap-2">
          <Label htmlFor="deposit-min">Minimum</Label>
          <Input
            id="deposit-min"
            value={draft.min}
            disabled={!canEdit}
            onChange={(e) => setDraft({ ...draft, min: e.target.value })}
            placeholder="$5"
          />
          <p className="text-xs text-muted-foreground">The least a deposit can be.</p>
        </div>
        <div className="grid gap-2">
          <Label htmlFor="deposit-max">Maximum</Label>
          <Input
            id="deposit-max"
            value={draft.max}
            disabled={!canEdit}
            onChange={(e) => setDraft({ ...draft, max: e.target.value })}
            placeholder="$1,000"
          />
          <p className="text-xs text-muted-foreground">The most one deposit can be.</p>
        </div>
      </div>
      {"error" in result && canEdit && (
        <p className="text-sm text-destructive" role="status">
          {result.error}
        </p>
      )}
      {canEdit && (
        <div className="flex flex-wrap items-center gap-2">
          <Button type="submit" disabled={saving || !dirty || "error" in result}>
            {saving ? "Saving…" : "Save deposit settings"}
          </Button>
          {footer}
        </div>
      )}
    </form>
  );
}
